import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import MagnifyIcon from '@material-ui/icons/Search';

import AnalyticsBase from '../AnalyticsBase';
import AnalyticsFilterBar from '../AnalyticsFilterBar';
import EmptyState from '../../common/EmptyState';
import BreadcrumbNav from '../../nav/BreadcrumbNav';

import { initializeAnalytics } from '../../../state/reducer/analytics/actionCreators/initializeAnalytics';
import { ParamUrlBuilder } from '../../../lib/ParamUrlBuilder';
import * as DateUtils from '../../../lib/dateUtils';
import * as LibraryState from '../../../state/reducer/library/library';
import * as AnalyticsState from '../../../state/reducer/analytics/analytics';
import * as AppState from '../../../state/reducer/app/appState';

import Console from '../../../lib/debug';

const PAGE_KEY = 'lift-one-rep-max-history';
export const DEFAULT_SEARCH_DATE_OFFSET = 30;

export class LiftOneRepMaxHistoryViewClass extends Component {
    constructor(props) {
        super(props);
        LibraryState.fetchAthletes();
        LibraryState.loadExercises();
    }

    componentDidUpdate(prevProps, prevState) {
        this.loadAnalytics();
    }

    loadAnalytics() {
        const dataParams = this.getDataParams();
        if (dataParams.user_ids.length === 0) {
            AppState.stopLoadingAnalytics();
            return;
        }
        if (!dataParams.exercise_id) {
            AppState.stopLoadingAnalytics();
            return;
        }

        this.props.dispatch(initializeAnalytics(PAGE_KEY, dataParams));
    }

    getAthleteId = () => {
        const params = this.getDataParams();
        if (params.user_ids) {
            return parseInt(params.user_ids, 10);
        }
        return undefined;
    };

    getExerciseId = () => {
        const params = this.getDataParams();
        if (params.exercise_id) {
            return parseInt(params.exercise_id, 10);
        }
        return undefined;
    };

    getStartDate = () => {
        const dataParams = this.getDataParams();
        return moment(dataParams.date_start);
    };

    getEndDate = () => {
        const dataParams = this.getDataParams();
        return moment(dataParams.date_end);
    };

    onSelectAthlete = (e, { suggestion }) => {
        const { id } = suggestion;
        this.setSearchParams({
            user_id: id
        });
    };

    onSelectExercise = (e, { suggestion }) => {
        const { id } = suggestion;
        this.setSearchParams({
            exercise_id: id
        });
    };

    getInitialState = () => {
        const athleteId = this.getAthleteId();
        const exericseId = this.getExerciseId();

        if (athleteId && exericseId) {
            return null;
        }

        return (
            <div style={{ paddingTop: 60 }}>
                <EmptyState
                    text="Select Athlete and Exercise to Run Lift History"
                    icon={<MagnifyIcon style={{ fontSize: 81 }} />}
                />
            </div>
        );
    };

    setSearchParams = params => {
        const existingParams = AnalyticsState.getSearchParams();
        AnalyticsState.updateSearchParams({
            ...existingParams,
            ...params
        });
    };

    getDataParams = () => {
        const searchParams = AnalyticsState.getSearchParams();

        let endDate = searchParams.end_date;
        if (!DateUtils.isValidDateString(endDate)) {
            endDate = moment();
            endDate = moment(endDate, DateUtils.CALENDAR_FORMAT);
        }

        let startDate = searchParams.start_date;
        if (!DateUtils.isValidDateString(startDate)) {
            startDate = moment(endDate).subtract(
                DEFAULT_SEARCH_DATE_OFFSET,
                'd'
            );
            startDate = moment(startDate, DateUtils.CALENDAR_FORMAT);
        }

        let userIds = searchParams.user_id;
        let userIdArray = [];
        if (userIds) {
            userIdArray.push(userIds);
        }

        const exercise_id = searchParams.exercise_id;
        let useMetric = parseInt(searchParams.use_metric, 10) === 1;
        if (searchParams.use_metric === undefined) {
            useMetric = this.props && this.props.useMetric;
        }

        return {
            date_start: moment(startDate).format('YYYY-MM-DD'),
            date_end: moment(endDate).format('YYYY-MM-DD'),
            user_ids: userIdArray,
            exercise_id,
            use_metric: useMetric
        };
    };

    onSelectDateRange = ({ startDate, endDate }) => {
        const formattedStartDate = startDate.format(DateUtils.CALENDAR_FORMAT);
        if (!endDate) {
            this.setSearchParams({
                start_date: formattedStartDate,
                end_date: formattedStartDate
            });
            return;
        }

        this.setSearchParams({
            start_date: formattedStartDate,
            end_date: endDate.format(DateUtils.CALENDAR_FORMAT)
        });
    };

    handleUseMetricChange = useMetric => {
        this.setSearchParams({
            use_metric: useMetric ? 1 : 0
        });
    };

    onClick = (e, row) => {
        const dataParams = this.getDataParams();
        const paramUrlBuilder = new ParamUrlBuilder('/analytics/lift-history/');
        paramUrlBuilder
            .setUserId(row.user_id)
            .setStartDate(this.getStartDate())
            .setEndDate(this.getEndDate())
            .setExerciseId(dataParams.exercise_id)
            .setUseMetric(dataParams.use_metric);

        AnalyticsState.navigate(paramUrlBuilder.buildUrl());
    };

    getPageTitle = () => {
        let title =
            this.props.metaTree &&
            this.props.metaTree.instances &&
            this.props.metaTree.instances[PAGE_KEY] &&
            this.props.metaTree.instances[PAGE_KEY].title;
        if (title) {
            return title;
        }
        return '';
    };

    render() {
        Console.render('LiftOneRepMaxHistoryView');
        const initialState = this.getInitialState();

        const rowEvents = {
            onClick: this.onClick
        };

        const crumbs = [
            {
                displayString: 'ANALYTICS',
                link: `/analytics`,
                editable: false
            },
            {
                displayString: this.getPageTitle(),
                link: null,
                editable: false
            }
        ];

        const dataParams = this.getDataParams();

        return (
            <div className="has-filter-bar">
                <BreadcrumbNav crumbs={crumbs} />
                <AnalyticsFilterBar
                    dateRangePicker={{
                        startDate: this.getStartDate(),
                        endDate: this.getEndDate(),
                        onChange: this.onSelectDateRange
                    }}
                    athleteSelector={{
                        selectedId: this.getAthleteId(),
                        onSelect: this.onSelectAthlete,
                        isFocused: false
                    }}
                    exerciseSelector={{
                        selectedId: this.getExerciseId(),
                        onSelect: this.onSelectExercise,
                        isFocused: false
                    }}
                    useMetricSelector={{
                        useMetric: dataParams.use_metric,
                        onChange: this.handleUseMetricChange
                    }}
                />
                <AnalyticsBase rowEvents={rowEvents} includeGraph={true} />
                {initialState}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        ...ownProps,
        athletesTree: state.library.athletesTree,
        exerciseRef: state.library.exerciseRef,
        metaTree: state.analytics.metaTree,
        useMetric: state.app.loggedInUser.use_metric
    };
};

export default connect(mapStateToProps)(LiftOneRepMaxHistoryViewClass);
